import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import type { ModalProps } from 'reactstrap';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

export enum ConfirmModalActionPair {
  OkCancel,
  YesNo,
  Custom
}

const getTitleOK = (actionPair: ConfirmModalActionPair, customTitle: string): string | null => {
  switch (actionPair) {
    case ConfirmModalActionPair.OkCancel:
      return 'OK';
    case ConfirmModalActionPair.YesNo:
      return 'Yes';
    case ConfirmModalActionPair.Custom:
      return customTitle;
    default:
      return null;
  }
};

const getTitleCancel = (actionPair: ConfirmModalActionPair, customTitle: string): string | null => {
  switch (actionPair) {
    case ConfirmModalActionPair.OkCancel:
      return 'Cancel';
    case ConfirmModalActionPair.YesNo:
      return 'No';
    case ConfirmModalActionPair.Custom:
      return customTitle;
    default:
      return null;
  }
};

export type ConfirmModalProps = {
  isOpen: boolean;
  actionPair?: ConfirmModalActionPair;
  title?: string;
  children?: ReactNode | string;
  handleClickOK?: () => void;
  handleClickCancel?: () => void;
  buttonTitleOK?: string;
  buttonTitleCancel?: string;
  showOKButton?: boolean;
  showCancelButton?: boolean;
  modal?: ModalProps;
};

export default function ConfirmModal({
  isOpen,
  title,
  children,
  buttonTitleOK,
  buttonTitleCancel,
  modal,
  handleClickOK = (): void => {},
  handleClickCancel = (): void => {},
  actionPair = ConfirmModalActionPair.OkCancel,
  showOKButton = true,
  showCancelButton = true
}: React.PropsWithChildren<ConfirmModalProps>): ReactElement {
  return (
    <Modal isOpen={isOpen} {...modal}>
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>{children}</ModalBody>
      <ModalFooter>
        {showOKButton && (
          <Button color="primary" onClick={handleClickOK} data-testid="ok-button">
            {getTitleOK(actionPair, buttonTitleOK || '')}
          </Button>
        )}
        {showCancelButton && (
          <Button color="primary" outline onClick={handleClickCancel} data-testid="cancel-button">
            {getTitleCancel(actionPair, buttonTitleCancel || '')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
